import type { ReactNode } from "react";
import { Link, matchPath, useLocation } from "../lib/router.js";
import { useSession } from "../lib/session.js";
import {
  IconBody,
  IconClipboard,
  IconDumbbell,
  IconHeart,
  IconLog,
  IconMore,
  IconProgress,
  IconSettings,
  IconShield,
  IconSparkles,
  IconTarget,
  IconTimeline,
  IconToday,
  IconUser
} from "./icons.js";

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
}

// Desktop sidebar information architecture.
const PRIMARY_NAV: NavItem[] = [
  { to: "/today", label: "Today", icon: <IconToday className="nav-icon" /> },
  { to: "/goals", label: "Goals", icon: <IconTarget className="nav-icon" /> },
  { to: "/nutrition", label: "Nutrition", icon: <IconClipboard className="nav-icon" /> },
  { to: "/training", label: "Training", icon: <IconDumbbell className="nav-icon" /> },
  { to: "/progress", label: "Progress", icon: <IconProgress className="nav-icon" /> },
  { to: "/timeline", label: "Timeline", icon: <IconTimeline className="nav-icon" /> },
  { to: "/recovery", label: "Recovery", icon: <IconHeart className="nav-icon" /> },
  { to: "/body", label: "Body", icon: <IconBody className="nav-icon" /> },
  { to: "/insights", label: "Insights", icon: <IconSparkles className="nav-icon" /> },
  { to: "/journal", label: "Journal", icon: <IconClipboard className="nav-icon" /> }
];

// Mobile bottom navigation — five destinations with a central Quick Log.
const BOTTOM_NAV: (NavItem & { key: string })[] = [
  { key: "today", to: "/today", label: "Today", icon: <IconToday className="nav-icon" /> },
  { key: "goals", to: "/goals", label: "Goals", icon: <IconTarget className="nav-icon" /> },
  { key: "log", to: "/log", label: "Log", icon: <IconLog className="nav-icon" /> },
  { key: "progress", to: "/progress", label: "Progress", icon: <IconProgress className="nav-icon" /> },
  { key: "more", to: "/more", label: "More", icon: <IconMore className="nav-icon" /> }
];

function isActive(path: string, to: string): boolean {
  return matchPath(to, path) !== null || path.startsWith(to + "/");
}

export function AppShell({ children }: { children: ReactNode }) {
  const path = useLocation();
  const { user } = useSession();

  return (
    <div className="app-shell">
      {/* Desktop sidebar */}
      <nav className="sidebar" aria-label="Primary">
        <Link to="/today" className="sidebar-brand">
          <img src="/logo.png" alt="" />
          <span>
            <span className="brand-name">ClovaForm</span>
            <br />
            <span className="brand-tag">Build the system</span>
          </span>
        </Link>

        {PRIMARY_NAV.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className={`nav-link${isActive(path, item.to) ? " active" : ""}`}
          >
            {item.icon}
            <span>{item.label}</span>
          </Link>
        ))}

        {user?.role === "admin" && (
          <>
            <div className="nav-section-label">Admin</div>
            <Link
              to="/admin"
              className={`nav-link${isActive(path, "/admin") ? " active" : ""}`}
            >
              <IconShield className="nav-icon" />
              <span>Administration</span>
            </Link>
          </>
        )}

        <div className="nav-section-label">You</div>
        <Link
          to="/life-profile"
          className={`nav-link${isActive(path, "/life-profile") ? " active" : ""}`}
        >
          <IconClipboard className="nav-icon" />
          <span>Life Profile</span>
        </Link>
        <Link
          to="/settings"
          className={`nav-link${isActive(path, "/settings") ? " active" : ""}`}
        >
          <IconSettings className="nav-icon" />
          <span>Settings</span>
        </Link>

        <div className="sidebar-footer">
          <Link to="/settings" className="nav-link">
            <IconUser className="nav-icon" />
            <span>{user?.displayName ?? user?.username}</span>
          </Link>
        </div>
      </nav>

      {/* Mobile top bar */}
      <header className="topbar">
        <Link to="/today" className="topbar-brand">
          <img src="/logo.png" alt="" />
          <span>ClovaForm</span>
        </Link>
      </header>

      <main className="app-main">
        <div className="content">{children}</div>
      </main>

      {/* Mobile bottom navigation */}
      <nav className="bottom-nav" aria-label="Primary">
        {BOTTOM_NAV.map((item) => {
          const active = isActive(path, item.to);
          const isLog = item.key === "log";
          return (
            <Link
              key={item.key}
              to={item.to}
              className={`bottom-nav-item${active ? " active" : ""}${isLog ? " is-log" : ""}`}
            >
              {isLog ? <span className="log-bubble">{item.icon}</span> : item.icon}
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
